import { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';

interface SchedulePoint {
  /** Hora LOCAL del punto, ya formateada (`08:00`). */
  time: string;
  /** Estación, terminal o puerto tal como lo publica el proveedor. */
  place: string;
  /**
   * Salvedad sobre la hora, en volado: el día de llegada cuando no es el de
   * salida. Un nocturno que no lo aclara se lee como un viaje de dos horas.
   */
  mark?: string;
}

interface Props {
  departure: SchedulePoint;
  arrival: SchedulePoint;
  /** Duración total del viaje, ya formateada ("2h 30m"). */
  duration: string;
  /**
   * Lo que hay que saber del trayecto en sí: conexiones, tiempo de check-in.
   * Va debajo de la línea de duración, donde el ojo ya está.
   */
  note?: ReactNode;
}

/**
 * El bloque de horarios del arquetipo de salidas (trenes, ferrys, buses). En
 * estas verticales el asesor escanea por HORA, no por título: las horas son lo
 * más grande de la card y la estación cuelga de cada una.
 *
 * La duración vive en el medio, sobre un riel sin color de marca — es
 * orientación, no decisión (el violeta queda para el CTA).
 */
export function ScheduleTimes({ departure, arrival, duration, note }: Props) {
  const point = ({ time, place, mark }: SchedulePoint) => (
    <Box minWidth={0}>
      <Typography
        component="p"
        sx={{
          fontFamily: 'monospace',
          fontSize: 19,
          fontWeight: 600,
          lineHeight: 1.2,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {time}
        {mark && (
          <Typography
            component="sup"
            color="warning.dark"
            sx={{ fontSize: 10, fontWeight: 600, ml: '2px' }}
          >
            {mark}
          </Typography>
        )}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontSize: 12, mt: '1px', maxWidth: 200 }}
      >
        {place}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'auto minmax(56px, 1fr) auto',
          sm: 'auto minmax(80px, 260px) auto 1fr',
        },
        gap: 1.5,
        alignItems: 'start',
      }}
    >
      {point(departure)}

      <Box sx={{ pt: '12px' }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            fontSize: 11,
            textAlign: 'center',
            mb: '2px',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {duration}
        </Typography>
        <Box
          sx={(theme) => ({
            position: 'relative',
            height: '2px',
            borderRadius: '1px',
            bgcolor: theme.palette.divider,
            '&::before, &::after': {
              content: '""',
              position: 'absolute',
              top: '-2.5px',
              width: '7px',
              height: '7px',
              borderRadius: '50%',
              bgcolor: theme.palette.background.paper,
              border: '2px solid',
              borderColor: theme.palette.text.disabled,
            },
            '&::before': { left: '-2px' },
            '&::after': { right: '-2px' },
          })}
        />
        {note && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: 'block', fontSize: 10.5, textAlign: 'center', mt: '3px' }}
          >
            {note}
          </Typography>
        )}
      </Box>

      {point(arrival)}
    </Box>
  );
}
